import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addProduct, removeProduct } from "../redux/wishSlice";
import ProductImage from "../components/ProductImage";
import { FiHeart, FiArrowLeft, FiMaximize2 } from 'react-icons/fi';
import { FaStar, FaFire } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import styles from "../styles/productCard.module.css";

const NEW_BADGE_DAYS = 45;
const BESTSELLER_MIN_SOLD = 15;

const COLOR_SWATCHES = {
  'ترابية': '#b08968',
  'فاتحة': '#e8ded4',
  'داكنة': '#4a3a2a',
  'الوان': 'conic-gradient(#e76f51, #e9c46a, #2a9d8f, #7b6bb7, #e76f51)',
  'أسود': '#1f1b17',
  'أبيض': '#ffffff'
};

// A product can be in several categories; older data (e.g. saved wishlists)
// only has the single `category_name`.
export const categoryNames = (product) =>
  product?.categories?.length
    ? product.categories.map((c) => c.name)
    : product?.category_name
      ? [product.category_name]
      : [];

/** "First category" or "First category +2" */
export const categoryLabel = (product) => {
  const names = categoryNames(product);
  if (!names.length) return null;
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
};

const sizesLabel = (count) => {
  if (count === 1) return 'مقاس واحد';
  if (count === 2) return 'مقاسان';
  return `${count} مقاسات`;
};

const ProductCard = ({ products, lowestPrices, onSelect }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wishlist = useSelector((state) => state.wishlist.wishlist);

  const add = (product, lowestPrice) => {
    dispatch(addProduct({product: product, lowestPrice: lowestPrice}))
  };

  const remove = (product) => {
    dispatch(removeProduct(product))
  };

  return (
    <div className={styles.grid}>
      {products.map((product) => {
        const lowestPrice = lowestPrices?.find(p => p.product_id === product.product_id)?.min_price;
        const inWishlist = wishlist.some(item => item.product.product_id === product.product_id);

        const isNew = product.created_at &&
          (Date.now() - new Date(product.created_at).getTime()) < NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
        const isBestseller = product.total_sold >= BESTSELLER_MIN_SOLD;
        const swatch = COLOR_SWATCHES[product.color];

        return (
          <article
            key={product.product_id}
            className={styles.card}
            onClick={() => onSelect ? onSelect(product) : navigate(`/product/${product.product_id}`)}
          >
            <div className={styles.media}>
              <ProductImage
                url={product.image_url}
                alt={product.name}
                loading="lazy"
                className={styles.image}
              />
              <div className={styles.mediaOverlay}>
                <span className={styles.quickView}>عرض التفاصيل</span>
              </div>

              <div className={styles.badges}>
                {isNew && (
                  <span className={`${styles.badge} ${styles.badgeNew}`}>
                    <HiSparkles /> جديد
                  </span>
                )}
                {isBestseller && (
                  <span className={`${styles.badge} ${styles.badgeHot}`}>
                    <FaFire /> الأكثر مبيعاً
                  </span>
                )}
              </div>

              <button
                aria-label={inWishlist ? 'إزالة من المفضلة' : 'إضافة الى المفضلة'}
                className={`${styles.wishBtn} ${inWishlist ? styles.wishBtnActive : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  inWishlist ? remove(product) : add(product, lowestPrice);
                }}
              >
                <FiHeart className={styles.wishIcon} />
              </button>
            </div>

            <div className={styles.body}>
              <div className={styles.tags}>
                {categoryLabel(product) && (
                  <span className={styles.categoryTag} title={categoryNames(product).join('، ')}>
                    {categoryLabel(product)}
                  </span>
                )}
                {swatch && (
                  <span className={styles.colorTag}>
                    <span className={styles.colorDot} style={{ background: swatch }} />
                    {product.color}
                  </span>
                )}
              </div>

              <h3 className={styles.name}>{product.name}</h3>

              <div className={styles.metaRow}>
                {product.rating != null && (
                  <span className={styles.rating}>
                    <FaStar className={styles.starIcon} />
                    <strong>{product.rating}</strong>
                    <span className={styles.ratingCount}>({product.reviews_count})</span>
                  </span>
                )}
                {product.sizes_count > 0 && (
                  <span className={styles.sizes}>
                    <FiMaximize2 />
                    {sizesLabel(product.sizes_count)}
                  </span>
                )}
              </div>

              <div className={styles.priceRow}>
                <p className={styles.price}>
                  {lowestPrice != null && (
                    <>
                      <span className={styles.priceLabel}>يبدأ من</span>
                      <span className={styles.priceValue}>{lowestPrice} ₪</span>
                    </>
                  )}
                </p>
                <span className={styles.arrowBtn}>
                  <FiArrowLeft />
                </span>
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
};

export default ProductCard;
